import { Injectable } from '@angular/core';
import {
  Channel,
  ChatMessage,
  ConvoType,
  IParticipant,
  MsgType,
  SystemMessageData,
  SystemMsgType,
  TxnMessageData
} from '@b3networks/api/chat';
import { Txn, TxnChannel } from '@b3networks/api/inbox';
import { ConversationGroup } from '@b3networks/api/workspace';
import { WindownActiveService } from '@b3networks/shared/common';
import { ConvoHelperService, SupportedConvo } from '../../adapter/convo-helper.service';
import { CacheProcessorUtils } from './cache-processor.utils';

export const SUPPORTED_CONVOS = [
  ConvoType.whatsapp,
  ConvoType.LIVECHAT,
  ConvoType.INTERNAL_SPACE,
  ConvoType.email,
  ConvoType.call
];

export const SUPPORTED_CHANNEL = [
  ConvoType.direct,
  ConvoType.groupchat,
  ConvoType.personal,
  ConvoType.THREAD,
  ConvoType.NEWSFEED,
  ConvoType.KB
];

@Injectable({ providedIn: 'root' })
export class MessageProcessorUtils {
  private readonly customerChannelTypes = [ConvoType.whatsapp, ConvoType.LIVECHAT];
  private readonly notificationChannelTypes = [ConvoType.LIVECHAT, ConvoType.INTERNAL_SPACE, ConvoType.whatsapp];

  constructor(
    private convoHelper: ConvoHelperService,
    private windownActiveService: WindownActiveService,
    private cache: CacheProcessorUtils
  ) {}

  isCustomerChannelMessage(message: ChatMessage): boolean {
    return this.customerChannelTypes.includes(message.ct);
  }

  shouldPlayNotificationSound(convoType: ConvoType): boolean {
    return this.notificationChannelTypes.includes(convoType);
  }

  checkWindowFocus(convo: SupportedConvo): boolean {
    const activeId = this.convoHelper.getActiveByChannel(convo);
    const windowActive = this.windownActiveService.isWindowActive;
    return windowActive && activeId === convo.id;
  }

  getSystemMessageData(message: ChatMessage): SystemMessageData | undefined {
    if (message.mt !== MsgType.system || !message.body?.data) {
      return undefined;
    }
    return <SystemMessageData>message.body.data;
  }

  getTxnMessageData(message: ChatMessage): TxnMessageData | undefined {
    if (!message?.body?.data) {
      return undefined;
    }
    return message.body.data as TxnMessageData;
  }

  getConversationId(convo: SupportedConvo): string {
    return convo instanceof ConversationGroup ? convo.conversationGroupId : convo.id;
  }

  isSystemMessageRelatedToMe(message: ChatMessage, systemType: SystemMsgType): boolean {
    const systemData = this.getSystemMessageData(message);
    if (!systemData || systemData.type !== systemType) {
      return false;
    }

    switch (systemType) {
      case SystemMsgType.join:
        return systemData.join?.includes(this.cache.meUserId) || false;
      case SystemMsgType.leave:
        return systemData.leave?.includes(this.cache.meUserId) || false;
      case SystemMsgType.threadJoin:
        return systemData.threadJoin?.userId === this.cache.meUserId;
      default:
        return false;
    }
  }

  getConversationDisplayName(convo: SupportedConvo): string {
    if (convo instanceof Channel) {
      return convo.name || convo.displayName || 'Unnamed Channel';
    } else if (convo instanceof ConversationGroup) {
      return (
        convo.customerInfo?.name ||
        convo.customerInfo?.email ||
        `Conversation ${convo.conversationGroupId.substring(0, 8)}`
      );
    }
    return 'Unknown Conversation';
  }

  isInternalChannelMessage(message: ChatMessage): boolean {
    return SUPPORTED_CHANNEL.includes(message.ct);
  }

  createParticipantsFromUserIds(userIds: string[]): IParticipant[] {
    return userIds.map(userId => ({
      userID: userId,
      role: ''
    }));
  }

  shouldTrackAction(message: ChatMessage, txn: Txn, currentUserIdentity: string): boolean {
    if (!txn || txn.isClosedV2) {
      return false;
    }

    // Handle system messages
    if (message.mt === MsgType.system) {
      const data = this.getTxnMessageData(message);
      if (!data) return false;

      switch (data.type) {
        case SystemMsgType.created:
          return data.channel === TxnChannel.supportCenter && data.caseInfo?.createdByIdentity === currentUserIdentity;

        case SystemMsgType.assigned:
          return data.affectedUuid === currentUserIdentity;

        case SystemMsgType.unassigned:
          return data.affectedUuid === currentUserIdentity;

        case SystemMsgType.createComment:
          return data.triggeredByUuid !== currentUserIdentity && data.triggeredByOrgUuid !== currentUserIdentity;

        default:
          return false;
      }
    }

    // Handle regular messages
    if ([ConvoType.LIVECHAT, ConvoType.whatsapp].includes(message.ct)) {
      return message.user !== currentUserIdentity;
    }

    if (ConvoType.INTERNAL_SPACE === message.ct && message.hasMention) {
      return true;
    }

    return false;
  }
}
